import logger from "./logger";
/**
 * Maps country codes to default coordinates (latitude, longitude)
 * Based on ISO 3166-1 alpha-2 country codes
 * Coordinates are centered on the country's capital or major city
 */
export const countryCoordinatesMap = {
  // Africa
  GH: { latitude: 5.55, longitude: -0.1963, name: "Ghana" },
  KE: { latitude: -1.2921, longitude: 36.8219, name: "Kenya" },
  NG: { latitude: 9.082, longitude: 7.498, name: "Nigeria" },
  ZA: { latitude: -25.7479, longitude: 28.2293, name: "South Africa" },
  EG: { latitude: 30.0444, longitude: 31.2357, name: "Egypt" },
  TZ: { latitude: -6.369, longitude: 34.8888, name: "Tanzania" },
  UG: { latitude: 0.3476, longitude: 32.5825, name: "Uganda" },
  RW: { latitude: -1.9441, longitude: 30.0619, name: "Rwanda" },
  ET: { latitude: 9.032, longitude: 38.7635, name: "Ethiopia" },
  MA: { latitude: 34.0209, longitude: -6.8416, name: "Morocco" },
  DZ: { latitude: 28.0339, longitude: 1.6596, name: "Algeria" },

  // Americas
  US: { latitude: 38.9072, longitude: -77.0369, name: "United States" },
  CA: { latitude: 45.4215, longitude: -75.6972, name: "Canada" },
  MX: { latitude: 19.4326, longitude: -99.1332, name: "Mexico" },
  BR: { latitude: -15.7801, longitude: -47.9292, name: "Brazil" },
  AR: { latitude: -34.6037, longitude: -58.3816, name: "Argentina" },

  // Europe
  GB: { latitude: 51.5074, longitude: -0.1278, name: "United Kingdom" },
  FR: { latitude: 48.8566, longitude: 2.3522, name: "France" },
  DE: { latitude: 52.52, longitude: 13.405, name: "Germany" },
  IT: { latitude: 41.9028, longitude: 12.4964, name: "Italy" },
  ES: { latitude: 40.4168, longitude: -3.7038, name: "Spain" },
  NL: { latitude: 52.3676, longitude: 4.9041, name: "Netherlands" },
  BE: { latitude: 50.8503, longitude: 4.3517, name: "Belgium" },
  AT: { latitude: 48.2082, longitude: 16.3738, name: "Austria" },
  PT: { latitude: 38.7223, longitude: -9.1393, name: "Portugal" },
  IE: { latitude: 53.3498, longitude: -6.2603, name: "Ireland" },
  CH: { latitude: 46.948, longitude: 7.4474, name: "Switzerland" },
  SE: { latitude: 59.3293, longitude: 18.0686, name: "Sweden" },
  NO: { latitude: 59.9139, longitude: 10.7522, name: "Norway" },
  DK: { latitude: 55.6761, longitude: 12.5683, name: "Denmark" },
  PL: { latitude: 52.2297, longitude: 21.0122, name: "Poland" },

  // Asia
  CN: { latitude: 39.9042, longitude: 116.4074, name: "China" },
  JP: { latitude: 35.6762, longitude: 139.6503, name: "Japan" },
  IN: { latitude: 28.6139, longitude: 77.209, name: "India" },
  SG: { latitude: 1.3521, longitude: 103.8198, name: "Singapore" },
  HK: { latitude: 22.3193, longitude: 114.1694, name: "Hong Kong" },
  KR: { latitude: 37.5665, longitude: 126.978, name: "South Korea" },
  TH: { latitude: 13.7563, longitude: 100.5018, name: "Thailand" },
  MY: { latitude: 3.139, longitude: 101.6869, name: "Malaysia" },
  ID: { latitude: -6.2088, longitude: 106.8456, name: "Indonesia" },
  PH: { latitude: 14.5995, longitude: 120.9842, name: "Philippines" },
  VN: { latitude: 21.0285, longitude: 105.8542, name: "Vietnam" },

  // Middle East
  AE: { latitude: 25.2048, longitude: 55.2708, name: "UAE" },
  SA: { latitude: 24.7136, longitude: 46.6753, name: "Saudi Arabia" },
  IL: { latitude: 31.7683, longitude: 35.2137, name: "Israel" },

  // Oceania
  AU: { latitude: -35.2809, longitude: 149.13, name: "Australia" },
  NZ: { latitude: -41.2865, longitude: 174.7762, name: "New Zealand" },
};

/**
 * Get coordinates for a country
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {{ latitude: number, longitude: number, name: string } | null}
 */
export const getCoordinatesForCountry = countryCode => {
  if (!countryCode) return null;
  const upperCode = countryCode.toUpperCase();
  return countryCoordinatesMap[upperCode] || null;
};

const timezoneToCountryMap = {
  "Africa/Accra": "GH",
  "Africa/Addis_Ababa": "ET",
  "Africa/Algiers": "DZ",
  "Africa/Asmara": "ER",
  "Africa/Asmera": "ER",
  "Africa/Bamako": "ML",
  "Africa/Bangui": "CF",
  "Africa/Banjul": "GM",
  "Africa/Bissau": "GW",
  "Africa/Blantyre": "MW",
  "Africa/Brazzaville": "CG",
  "Africa/Bujumbura": "BI",
  "Africa/Cairo": "EG",
  "Africa/Casablanca": "MA",
  "Africa/Ceuta": "ES",
  "Africa/Conakry": "GN",
  "Africa/Dakar": "SN",
  "Africa/Dar_es_Salaam": "TZ",
  "Africa/Djibouti": "DJ",
  "Africa/Douala": "CM",
  "Africa/El_Aaiun": "EH",
  "Africa/Freetown": "SL",
  "Africa/Gaborone": "BW",
  "Africa/Harare": "ZW",
  "Africa/Johannesburg": "ZA",
  "Africa/Juba": "SS",
  "Africa/Kampala": "UG",
  "Africa/Khartoum": "SD",
  "Africa/Kigali": "RW",
  "Africa/Kinshasa": "CD",
  "Africa/Lagos": "NG",
  "Africa/Libreville": "GA",
  "Africa/Lome": "TG",
  "Africa/Luanda": "AO",
  "Africa/Lubumbashi": "CD",
  "Africa/Lusaka": "ZM",
  "Africa/Malabo": "GQ",
  "Africa/Maputo": "MZ",
  "Africa/Maseru": "LS",
  "Africa/Mbabane": "SZ",
  "Africa/Mogadishu": "SO",
  "Africa/Monrovia": "LR",
  "Africa/Nairobi": "KE",
  "Africa/Ndjamena": "TD",
  "Africa/Niamey": "NE",
  "Africa/Nouakchott": "MR",
  "Africa/Ouagadougou": "BF",
  "Africa/Porto-Novo": "BJ",
  "Africa/Sao_Tome": "ST",
  "Africa/Timbuktu": "ML",
  "Africa/Tripoli": "LY",
  "Africa/Tunis": "TN",
  "Africa/Windhoek": "NA",

  "America/Adak": "US",
  "America/Anchorage": "US",
  "America/Anguilla": "AI",
  "America/Antigua": "AG",
  "America/Araguaina": "BR",
  "America/Argentina/Buenos_Aires": "AR",
  "America/Argentina/Catamarca": "AR",
  "America/Argentina/ComodRivadavia": "AR",
  "America/Argentina/Cordoba": "AR",
  "America/Argentina/Jujuy": "AR",
  "America/Argentina/La_Rioja": "AR",
  "America/Argentina/Mendoza": "AR",
  "America/Argentina/Rio_Gallegos": "AR",
  "America/Argentina/Salta": "AR",
  "America/Argentina/San_Juan": "AR",
  "America/Argentina/San_Luis": "AR",
  "America/Argentina/Tucuman": "AR",
  "America/Argentina/Ushuaia": "AR",
  "America/Aruba": "AW",
  "America/Asuncion": "PY",
  "America/Atikokan": "CA",
  "America/Atka": "US",
  "America/Bahia": "BR",
  "America/Bahia_Banderas": "MX",
  "America/Barbados": "BB",
  "America/Belem": "BR",
  "America/Belize": "BZ",
  "America/Blanc-Sablon": "CA",
  "America/Boa_Vista": "BR",
  "America/Bogota": "CO",
  "America/Boise": "US",
  "America/Buenos_Aires": "AR",
  "America/Cambridge_Bay": "CA",
  "America/Campo_Grande": "BR",
  "America/Cancun": "MX",
  "America/Caracas": "VE",
  "America/Catamarca": "AR",
  "America/Cayenne": "GF",
  "America/Cayman": "KY",
  "America/Chicago": "US",
  "America/Chihuahua": "MX",
  "America/Coral_Harbour": "CA",
  "America/Cordoba": "AR",
  "America/Costa_Rica": "CR",
  "America/Creston": "CA",
  "America/Cuiaba": "BR",
  "America/Curacao": "CW",
  "America/Danmarkshavn": "GL",
  "America/Dawson": "CA",
  "America/Dawson_Creek": "CA",
  "America/Denver": "US",
  "America/Detroit": "US",
  "America/Dominica": "DM",
  "America/Edmonton": "CA",
  "America/Eirunepe": "BR",
  "America/El_Salvador": "SV",
  "America/Ensenada": "MX",
  "America/Fort_Nelson": "CA",
  "America/Fort_Wayne": "US",
  "America/Fortaleza": "BR",
  "America/Glace_Bay": "CA",
  "America/Godthab": "GL",
  "America/Goose_Bay": "CA",
  "America/Grand_Turk": "TC",
  "America/Grenada": "GD",
  "America/Guadeloupe": "GP",
  "America/Guatemala": "GT",
  "America/Guayaquil": "EC",
  "America/Guyana": "GY",
  "America/Halifax": "CA",
  "America/Havana": "CU",
  "America/Hermosillo": "MX",
  "America/Indiana/Indianapolis": "US",
  "America/Indiana/Knox": "US",
  "America/Indiana/Marengo": "US",
  "America/Indiana/Petersburg": "US",
  "America/Indiana/Tell_City": "US",
  "America/Indiana/Vevay": "US",
  "America/Indiana/Vincennes": "US",
  "America/Indiana/Winamac": "US",
  "America/Indianapolis": "US",
  "America/Inuvik": "CA",
  "America/Iqaluit": "CA",
  "America/Jamaica": "JM",
  "America/Jujuy": "AR",
  "America/Juneau": "US",
  "America/Kentucky/Louisville": "US",
  "America/Kentucky/Monticello": "US",
  "America/Knox_IN": "US",
  "America/Kralendijk": "BQ",
  "America/La_Paz": "BO",
  "America/Lima": "PE",
  "America/Los_Angeles": "US",
  "America/Louisville": "US",
  "America/Lower_Princes": "SX",
  "America/Maceio": "BR",
  "America/Managua": "NI",
  "America/Manaus": "BR",
  "America/Marigot": "MF",
  "America/Martinique": "MQ",
  "America/Matamoros": "MX",
  "America/Mazatlan": "MX",
  "America/Mendoza": "AR",
  "America/Menominee": "US",
  "America/Merida": "MX",
  "America/Metlakatla": "US",
  "America/Mexico_City": "MX",
  "America/Miquelon": "PM",
  "America/Moncton": "CA",
  "America/Monterrey": "MX",
  "America/Montevideo": "UY",
  "America/Montreal": "CA",
  "America/Montserrat": "MS",
  "America/Nassau": "BS",
  "America/New_York": "US",
  "America/Nipigon": "CA",
  "America/Nome": "US",
  "America/Noronha": "BR",
  "America/North_Dakota/Beulah": "US",
  "America/North_Dakota/Center": "US",
  "America/North_Dakota/New_Salem": "US",
  "America/Nuuk": "GL",
  "America/Ojinaga": "MX",
  "America/Panama": "PA",
  "America/Pangnirtung": "CA",
  "America/Paramaribo": "SR",
  "America/Phoenix": "US",
  "America/Port-au-Prince": "HT",
  "America/Port_of_Spain": "TT",
  "America/Porto_Acre": "BR",
  "America/Porto_Velho": "BR",
  "America/Puerto_Rico": "PR",
  "America/Punta_Arenas": "CL",
  "America/Rainy_River": "CA",
  "America/Rankin_Inlet": "CA",
  "America/Recife": "BR",
  "America/Regina": "CA",
  "America/Resolute": "CA",
  "America/Rio_Branco": "BR",
  "America/Rosario": "AR",
  "America/Santa_Isabel": "MX",
  "America/Santarem": "BR",
  "America/Santiago": "CL",
  "America/Santo_Domingo": "DO",
  "America/Sao_Paulo": "BR",
  "America/Scoresbysund": "GL",
  "America/Shiprock": "US",
  "America/Sitka": "US",
  "America/St_Barthelemy": "BL",
  "America/St_Johns": "CA",
  "America/St_Kitts": "KN",
  "America/St_Lucia": "LC",
  "America/St_Thomas": "VI",
  "America/St_Vincent": "VC",
  "America/Swift_Current": "CA",
  "America/Tegucigalpa": "HN",
  "America/Thule": "GL",
  "America/Thunder_Bay": "CA",
  "America/Tijuana": "MX",
  "America/Toronto": "CA",
  "America/Tortola": "VG",
  "America/Vancouver": "CA",
  "America/Virgin": "VI",
  "America/Whitehorse": "CA",
  "America/Winnipeg": "CA",
  "America/Yakutat": "US",
  "America/Yellowknife": "CA",

  "Antarctica/Casey": "AQ",
  "Antarctica/Davis": "AQ",
  "Antarctica/DumontDUrville": "AQ",
  "Antarctica/Macquarie": "AU",
  "Antarctica/Mawson": "AQ",
  "Antarctica/McMurdo": "AQ",
  "Antarctica/Palmer": "AQ",
  "Antarctica/Rothera": "AQ",
  "Antarctica/South_Pole": "AQ",
  "Antarctica/Syowa": "AQ",
  "Antarctica/Troll": "AQ",
  "Antarctica/Vostok": "AQ",

  "Arctic/Longyearbyen": "SJ",

  "Asia/Aden": "YE",
  "Asia/Almaty": "KZ",
  "Asia/Amman": "JO",
  "Asia/Anadyr": "RU",
  "Asia/Aqtau": "KZ",
  "Asia/Aqtobe": "KZ",
  "Asia/Ashgabat": "TM",
  "Asia/Ashkhabad": "TM",
  "Asia/Atyrau": "KZ",
  "Asia/Baghdad": "IQ",
  "Asia/Bahrain": "BH",
  "Asia/Baku": "AZ",
  "Asia/Bangkok": "TH",
  "Asia/Barnaul": "RU",
  "Asia/Beirut": "LB",
  "Asia/Bishkek": "KG",
  "Asia/Brunei": "BN",
  "Asia/Calcutta": "IN",
  "Asia/Chita": "RU",
  "Asia/Choibalsan": "MN",
  "Asia/Chongqing": "CN",
  "Asia/Chungking": "CN",
  "Asia/Colombo": "LK",
  "Asia/Dacca": "BD",
  "Asia/Damascus": "SY",
  "Asia/Dhaka": "BD",
  "Asia/Dili": "TL",
  "Asia/Dubai": "AE",
  "Asia/Dushanbe": "TJ",
  "Asia/Famagusta": "CY",
  "Asia/Gaza": "PS",
  "Asia/Harbin": "CN",
  "Asia/Hebron": "PS",
  "Asia/Ho_Chi_Minh": "VN",
  "Asia/Hong_Kong": "HK",
  "Asia/Hovd": "MN",
  "Asia/Irkutsk": "RU",
  "Asia/Istanbul": "TR",
  "Asia/Jakarta": "ID",
  "Asia/Jayapura": "ID",
  "Asia/Jerusalem": "IL",
  "Asia/Kabul": "AF",
  "Asia/Kamchatka": "RU",
  "Asia/Karachi": "PK",
  "Asia/Kashgar": "CN",
  "Asia/Kathmandu": "NP",
  "Asia/Katmandu": "NP",
  "Asia/Khandyga": "RU",
  "Asia/Kolkata": "IN",
  "Asia/Krasnoyarsk": "RU",
  "Asia/Kuala_Lumpur": "MY",
  "Asia/Kuching": "MY",
  "Asia/Kuwait": "KW",
  "Asia/Macao": "MO",
  "Asia/Macau": "MO",
  "Asia/Magadan": "RU",
  "Asia/Makassar": "ID",
  "Asia/Manila": "PH",
  "Asia/Muscat": "OM",
  "Asia/Nicosia": "CY",
  "Asia/Novokuznetsk": "RU",
  "Asia/Novosibirsk": "RU",
  "Asia/Omsk": "RU",
  "Asia/Oral": "KZ",
  "Asia/Phnom_Penh": "KH",
  "Asia/Pontianak": "ID",
  "Asia/Pyongyang": "KP",
  "Asia/Qatar": "QA",
  "Asia/Qyzylorda": "KZ",
  "Asia/Rangoon": "MM",
  "Asia/Riyadh": "SA",
  "Asia/Saigon": "VN",
  "Asia/Sakhalin": "RU",
  "Asia/Samarkand": "UZ",
  "Asia/Seoul": "KR",
  "Asia/Shanghai": "CN",
  "Asia/Singapore": "SG",
  "Asia/Srednekolymsk": "RU",
  "Asia/Taipei": "TW",
  "Asia/Tashkent": "UZ",
  "Asia/Tbilisi": "GE",
  "Asia/Tehran": "IR",
  "Asia/Tel_Aviv": "IL",
  "Asia/Thimbu": "BT",
  "Asia/Thimphu": "BT",
  "Asia/Tokyo": "JP",
  "Asia/Tomsk": "RU",
  "Asia/Ujung_Pandang": "ID",
  "Asia/Ulaanbaatar": "MN",
  "Asia/Ulan_Bator": "MN",
  "Asia/Urumqi": "CN",
  "Asia/Ust-Nera": "RU",
  "Asia/Vientiane": "LA",
  "Asia/Vladivostok": "RU",
  "Asia/Yakutsk": "RU",
  "Asia/Yangon": "MM",
  "Asia/Yekaterinburg": "RU",
  "Asia/Yerevan": "AM",

  "Atlantic/Azores": "PT",
  "Atlantic/Bermuda": "BM",
  "Atlantic/Canary": "ES",
  "Atlantic/Cape_Verde": "CV",
  "Atlantic/Faeroe": "FO",
  "Atlantic/Faroe": "FO",
  "Atlantic/Jan_Mayen": "SJ",
  "Atlantic/Madeira": "PT",
  "Atlantic/Reykjavik": "IS",
  "Atlantic/South_Georgia": "GS",
  "Atlantic/St_Helena": "SH",
  "Atlantic/Stanley": "FK",

  "Australia/ACT": "AU",
  "Australia/Adelaide": "AU",
  "Australia/Brisbane": "AU",
  "Australia/Broken_Hill": "AU",
  "Australia/Canberra": "AU",
  "Australia/Currie": "AU",
  "Australia/Darwin": "AU",
  "Australia/Eucla": "AU",
  "Australia/Hobart": "AU",
  "Australia/LHI": "AU",
  "Australia/Lindeman": "AU",
  "Australia/Lord_Howe": "AU",
  "Australia/Melbourne": "AU",
  "Australia/NSW": "AU",
  "Australia/North": "AU",
  "Australia/Perth": "AU",
  "Australia/Queensland": "AU",
  "Australia/South": "AU",
  "Australia/Sydney": "AU",
  "Australia/Tasmania": "AU",
  "Australia/Victoria": "AU",
  "Australia/West": "AU",
  "Australia/Yancowinna": "AU",

  "Europe/Amsterdam": "NL",
  "Europe/Andorra": "AD",
  "Europe/Astrakhan": "RU",
  "Europe/Athens": "GR",
  "Europe/Belfast": "GB",
  "Europe/Belgrade": "RS",
  "Europe/Berlin": "DE",
  "Europe/Bratislava": "SK",
  "Europe/Brussels": "BE",
  "Europe/Bucharest": "RO",
  "Europe/Budapest": "HU",
  "Europe/Busingen": "DE",
  "Europe/Chisinau": "MD",
  "Europe/Copenhagen": "DK",
  "Europe/Dublin": "IE",
  "Europe/Gibraltar": "GI",
  "Europe/Guernsey": "GG",
  "Europe/Helsinki": "FI",
  "Europe/Isle_of_Man": "IM",
  "Europe/Istanbul": "TR",
  "Europe/Jersey": "JE",
  "Europe/Kaliningrad": "RU",
  "Europe/Kiev": "UA",
  "Europe/Kirov": "RU",
  "Europe/Lisbon": "PT",
  "Europe/Ljubljana": "SI",
  "Europe/London": "GB",
  "Europe/Luxembourg": "LU",
  "Europe/Madrid": "ES",
  "Europe/Malta": "MT",
  "Europe/Mariehamn": "AX",
  "Europe/Minsk": "BY",
  "Europe/Monaco": "MC",
  "Europe/Moscow": "RU",
  "Europe/Nicosia": "CY",
  "Europe/Oslo": "NO",
  "Europe/Paris": "FR",
  "Europe/Podgorica": "ME",
  "Europe/Prague": "CZ",
  "Europe/Riga": "LV",
  "Europe/Rome": "IT",
  "Europe/Samara": "RU",
  "Europe/San_Marino": "SM",
  "Europe/Sarajevo": "BA",
  "Europe/Saratov": "RU",
  "Europe/Simferopol": "UA",
  "Europe/Skopje": "MK",
  "Europe/Sofia": "BG",
  "Europe/Stockholm": "SE",
  "Europe/Tallinn": "EE",
  "Europe/Tirane": "AL",
  "Europe/Tiraspol": "MD",
  "Europe/Ulyanovsk": "RU",
  "Europe/Uzhgorod": "UA",
  "Europe/Vaduz": "LI",
  "Europe/Vatican": "VA",
  "Europe/Vienna": "AT",
  "Europe/Vilnius": "LT",
  "Europe/Volgograd": "RU",
  "Europe/Warsaw": "PL",
  "Europe/Zagreb": "HR",
  "Europe/Zaporozhye": "UA",
  "Europe/Zurich": "CH",

  "Indian/Antananarivo": "MG",
  "Indian/Chagos": "IO",
  "Indian/Christmas": "CX",
  "Indian/Cocos": "CC",
  "Indian/Comoro": "KM",
  "Indian/Kerguelen": "TF",
  "Indian/Mahe": "SC",
  "Indian/Maldives": "MV",
  "Indian/Mauritius": "MU",
  "Indian/Mayotte": "YT",
  "Indian/Reunion": "RE",

  "Pacific/Apia": "WS",
  "Pacific/Auckland": "NZ",
  "Pacific/Bougainville": "PG",
  "Pacific/Chatham": "NZ",
  "Pacific/Chuuk": "FM",
  "Pacific/Easter": "CL",
  "Pacific/Efate": "VU",
  "Pacific/Enderbury": "KI",
  "Pacific/Fakaofo": "TK",
  "Pacific/Fiji": "FJ",
  "Pacific/Funafuti": "TV",
  "Pacific/Galapagos": "EC",
  "Pacific/Gambier": "PF",
  "Pacific/Guadalcanal": "SB",
  "Pacific/Guam": "GU",
  "Pacific/Honolulu": "US",
  "Pacific/Johnston": "US",
  "Pacific/Kiritimati": "KI",
  "Pacific/Kosrae": "FM",
  "Pacific/Kwajalein": "MH",
  "Pacific/Majuro": "MH",
  "Pacific/Marquesas": "PF",
  "Pacific/Midway": "US",
  "Pacific/Nauru": "NR",
  "Pacific/Niue": "NU",
  "Pacific/Norfolk": "NF",
  "Pacific/Noumea": "NC",
  "Pacific/Pago_Pago": "AS",
  "Pacific/Palau": "PW",
  "Pacific/Pitcairn": "PN",
  "Pacific/Ponape": "FM",
  "Pacific/Port_Moresby": "PG",
  "Pacific/Rarotonga": "CK",
  "Pacific/Saipan": "MP",
  "Pacific/Samoa": "WS",
  "Pacific/Tahiti": "PF",
  "Pacific/Tarawa": "KI",
  "Pacific/Tongatapu": "TO",
  "Pacific/Truk": "FM",
  "Pacific/Wake": "US",
  "Pacific/Wallis": "WF",
  "Pacific/Yap": "FM",
};

/**
 * Get country code from browser timezone
 * @returns {string|null}
 */
export const getCountryFromTimezone = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezoneToCountryMap[tz] || null;
  } catch {
    return null;
  }
};

const IP_APIS = [
  {
    url: "https://ipapi.co/json/",
    parse: data => data.country_code || data.country || null,
  },
  {
    url: "https://ipinfo.io/json",
    parse: data => data.country || null,
  },
  {
    url: "https://api.country.is/",
    parse: data => data.country || null,
  },
];

const fetchWithTimeout = (url, timeoutMs = 5000) =>
  Promise.race([
    fetch(url, { mode: "cors" }),
    new Promise((_, reject) => setTimeout(() => reject(new Error(`timeout`)), timeoutMs)),
  ]);

/**
 * Detect country via IP using 3 geolocation APIs (race).
 * @returns {Promise<string|null>}
 */
export const detectCountryFromIP = async () => {
  const attempts = IP_APIS.map(async api => {
    try {
      const res = await fetchWithTimeout(api.url);
      if (!res.ok) return null;
      const data = await res.json();
      return api.parse(data);
    } catch {
      return null;
    }
  });

  const results = await Promise.allSettled(attempts);
  for (const r of results) {
    if (r.status === "fulfilled" && r.value) {
      return r.value;
    }
  }
  return null;
};

/**
 * Get user's country coordinates.
 * Priority: browser timezone → cached userLocation → IP geolocation (3 APIs).
 * No hard-coded fallback — returns null if all detection fails.
 * @returns {Promise<{ latitude: number, longitude: number, name: string } | null>}
 */
export const getUserCountryCoordinates = async () => {
  try {
    // 1. Debug test country override (from test dropdown)
    const debugTestCountry = localStorage.getItem("debug_test_country");
    if (debugTestCountry) {
      const testCountryCode = JSON.parse(debugTestCountry)?.code || debugTestCountry;
      if (testCountryCode) {
        const coords = getCoordinatesForCountry(testCountryCode);
        if (coords) return coords;
      }
    }

    // 2. Browser timezone (primary — synchronous fast path)
    const countryCode = getCountryFromTimezone();
    if (countryCode) {
      const coords = getCoordinatesForCountry(countryCode);
      if (coords) return coords;
    }

    // 3. Cached precise location from previous GPS/IP geolocation
    const locationStr = localStorage.getItem("userLocation");
    if (locationStr) {
      const location = JSON.parse(locationStr);
      if (location.latitude && location.longitude) {
        return {
          latitude: location.latitude,
          longitude: location.longitude,
          name: location.country || location.countryCode || "Unknown",
        };
      }
    }

    // 4. IP geolocation (fallback — 3 APIs)
    const ipCountryCode = await detectCountryFromIP();
    if (ipCountryCode) {
      const coords = getCoordinatesForCountry(ipCountryCode);
      if (coords) return coords;
    }

    return null;
  } catch (error) {
    logger.error("Error getting user country coordinates:", error);
    return null;
  }
};

export default countryCoordinatesMap;
